import React, { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';

/**
 * Strategy Step - Withdrawal strategies and UK-specific tax options
 */
export default function StrategyStep({ inputs, setInputs, country, currencySymbol, accounts, investments }) {
  // Calculate pension accounts and potential lump sum (UK only)
  const pensionInfo = useMemo(() => {
    if (country !== 'UK') return null;

    const pensionAccounts = accounts.filter(account => {
      const accountType = account.type?.toLowerCase() || '';
      return accountType.includes('pension') || accountType === 'sipp';
    });

    const totalPensionBalance = pensionAccounts.reduce((sum, account) => {
      const accountInvestments = investments.filter(inv => inv.accountId === account.id);
      return sum + accountInvestments.reduce((invSum, inv) => invSum + (inv.amount || 0), 0);
    }, 0);

    const UK_LUMP_SUM_ALLOWANCE = 268275;
    const potentialLumpSum = Math.min(totalPensionBalance * 0.25, UK_LUMP_SUM_ALLOWANCE);

    return {
      hasPensionAccounts: pensionAccounts.length > 0,
      pensionCount: pensionAccounts.length,
      pensionAccounts: pensionAccounts,
      totalPensionBalance,
      potentialLumpSum
    };
  }, [accounts, investments, country]);

  return (
    <div className="space-y-6">
      {/* UK-Specific: Tax-Free Lump Sum Options */}
      {country === 'UK' && (
        <Card>
          <CardHeader>
            <CardTitle>UK Pension: 25% Tax-Free Lump Sum Options</CardTitle>
            <CardDescription>
              Configure how you'll take your tax-free pension lump sum (capped at £268,275)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Show pension account status */}
            {!pensionInfo.hasPensionAccounts ? (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertDescription className="text-xs">
                  <strong>⚠ No Pension Accounts Detected</strong>
                  <br />
                  You need to add a Pension or SIPP account to use the tax-free lump sum feature.
                  Add accounts in the Accounts section and select type as "Pension" or "SIPP".
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="bg-green-50 border-green-200">
                <AlertDescription className="text-xs">
                  <strong>✓ Pension Accounts Found:</strong> {pensionInfo.pensionCount} account(s)
                  <br />
                  {pensionInfo.pensionAccounts?.map((acc, idx) => (
                    <span key={acc.id}>
                      - {acc.name || 'Unnamed'} ({acc.type})
                      {idx < pensionInfo.pensionAccounts.length - 1 && <br />}
                    </span>
                  ))}
                  <br />
                  <strong>Current Pension Balance:</strong> {currencySymbol}{pensionInfo.totalPensionBalance.toLocaleString()}
                  <br />
                  <strong>Your 25% Tax-Free Lump Sum:</strong> {currencySymbol}{pensionInfo.potentialLumpSum.toLocaleString()}
                  {pensionInfo.potentialLumpSum >= 268275 && ' (Capped at maximum LSA)'}
                </AlertDescription>
              </Alert>
            )}

            {/* Important Cap Information */}
            <Alert className={inputs.ukTaxFreeLumpSumOptions?.takeAsLumpSum ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200"}>
              <AlertDescription className="text-xs">
                <strong>Maximum Lump Sum Allowance (LSA):</strong> £268,275
                <br />
                {inputs.ukTaxFreeLumpSumOptions?.takeAsLumpSum ? (
                  <>
                    <strong className="text-blue-700">✓ Lump Sum ENABLED:</strong> Taking {currencySymbol}{pensionInfo?.potentialLumpSum.toLocaleString()} at retirement will reduce your portfolio immediately.
                    <br />
                    Your pension balance will go from {currencySymbol}{pensionInfo?.totalPensionBalance.toLocaleString()} to {currencySymbol}{(pensionInfo?.totalPensionBalance - pensionInfo?.potentialLumpSum).toLocaleString()}.
                  </>
                ) : (
                  <>
                    <strong>Phased Withdrawal:</strong> Spreading {currencySymbol}{pensionInfo?.potentialLumpSum.toLocaleString()} over {inputs.ukTaxFreeLumpSumOptions?.phasedYears || 5} years ({currencySymbol}{Math.round((pensionInfo?.potentialLumpSum || 0) / (inputs.ukTaxFreeLumpSumOptions?.phasedYears || 5)).toLocaleString()}/year).
                  </>
                )}
              </AlertDescription>
            </Alert>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="takeAsLumpSum"
                checked={inputs.ukTaxFreeLumpSumOptions?.takeAsLumpSum || false}
                onChange={(e) => {
                  setInputs(prev => ({
                    ...prev,
                    ukTaxFreeLumpSumOptions: {
                      ...prev.ukTaxFreeLumpSumOptions,
                      takeAsLumpSum: e.target.checked
                    }
                  }));
                }}
                className="h-4 w-4"
              />
              <Label htmlFor="takeAsLumpSum" className="text-sm cursor-pointer">
                Take all 25% as a lump sum at retirement (capped at £268,275)
              </Label>
            </div>

            {!inputs.ukTaxFreeLumpSumOptions?.takeAsLumpSum && (
              <div className="space-y-1">
                <Label className="text-xs">Spread over how many years?</Label>
                <Input
                  type="number"
                  value={inputs.ukTaxFreeLumpSumOptions?.phasedYears || 5}
                  onChange={(e) => {
                    setInputs(prev => ({
                      ...prev,
                      ukTaxFreeLumpSumOptions: {
                        ...prev.ukTaxFreeLumpSumOptions,
                        phasedYears: parseInt(e.target.value)
                      }
                    }));
                  }}
                  min="1"
                  max="10"
                  className="text-sm max-w-xs"
                />
                <p className="text-xs text-muted-foreground">
                  Phased withdrawals can be more tax-efficient. Total still capped at £268,275.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
